import {useMediaQuery} from '@chakra-ui/react';
import {unwrapResult} from '@reduxjs/toolkit';
import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom';
import {Col, Row, UiBox, UiImage, UiQuestionnaireLayout, UiSimpleGrid, UiText} from '~/components/elements';
import {AppCarousel} from '~/components/elements/AppCarousel';
import {UiCard} from '~/components/elements/UiCard';
import {QUERY_MOBILE} from '~/constants';
import {
  BIRTH_CONTROL_PATCH,
  BIRTH_CONTROL_REQUEST_KEYS,
  FREE_DELIVERY_KEY,
  METHOD_KEYS,
  PATCH_TOP_ICON,
  STANDARD_DELIVERY_KEY,
} from '~/constants/medical-questionnaires';
import {useInput, useNotification, useRequestState} from '~/hooks';
import {setQuestionnaireAnswer, setUserQuestionnaireId} from '~/redux/questionnaires/slice';
import {addQuestionnaire} from '~/redux/questionnaires/thunk';
import {PackItem} from '~/redux/questionnaires/types';
import {RootState} from '~/redux/root-reducer';
import {AppDispatch} from '~/redux/root-store';
import {getCurrentUserFromStorage} from '~/services/auth';
import {spacing} from '~/utils/theme/spacing';
import {requestOK} from '~/utils/thunk-api';

const Questionnaire13Screen = () => {
  const dispatch: AppDispatch = useDispatch();
  const history = useHistory();
  const {accessToken: savedToken} = getCurrentUserFromStorage();
  const {addMessage, addValidationMessage} = useNotification(dispatch);

  const {loading} = useRequestState(addQuestionnaire);

  const [isDesktop] = useMediaQuery(`(min-width: ${QUERY_MOBILE})`);

  const currentAnswer = useSelector(
    (state: RootState) => state.questionnaires?.collectData[BIRTH_CONTROL_REQUEST_KEYS.CHOOSE_PRODUCT_PACKAGE]?.data,
  );
  const chooseMedicationAnswer = useSelector(
    (state: RootState) => state.questionnaires?.collectData[BIRTH_CONTROL_REQUEST_KEYS.CHOOSE_MEDICATION]?.data,
  );
  const isBirthControlPatchSelected = chooseMedicationAnswer?.selectedOption === BIRTH_CONTROL_PATCH;

  const medicalQuestionnaireAnswer = useSelector((state: RootState) => state.questionnaires?.collectData);

  const medicationAnswer = useSelector((state: RootState) => state.questionnaires.selectedMedication);
  const packSizeList: PackItem[] = medicationAnswer?.packSize;

  const medicalQuestionnaire = useSelector((state: RootState) => state.questionnaires.medicalQuestionnaire);
  const {wpAuthUser} = medicalQuestionnaire;

  const questionnaires = medicalQuestionnaire?.questionnaires;
  const questionnaire = questionnaires?.find(i => i.key === BIRTH_CONTROL_REQUEST_KEYS.CHOOSE_PRODUCT_PACKAGE);
  const defaultPack = packSizeList.find(item => item?.isDefault);
  const {inputs, setInputs, validation} = useInput({
    productId: currentAnswer?.productId || defaultPack?.productId || '',
  });

  const selectedItemIndex = packSizeList.findIndex((i: PackItem) => i.productId === inputs.productId);

  const onClickSubmit = async () => {
    const questionnaireAnswer = {
      key: BIRTH_CONTROL_REQUEST_KEYS.CHOOSE_PRODUCT_PACKAGE,
      index: questionnaire?.index || '',
      data: inputs,
    };
    dispatch(setQuestionnaireAnswer(questionnaireAnswer));
    const resAction = await dispatch(
      addQuestionnaire({
        data: {
          data: {
            ...medicalQuestionnaireAnswer,
            [BIRTH_CONTROL_REQUEST_KEYS.CHOOSE_PRODUCT_PACKAGE]: {...questionnaireAnswer},
          },
          wpUserId: wpAuthUser?.wpUserId,
          methodKey: METHOD_KEYS.BIRTH_CONTROL_REQUEST,
        },
      }),
    );

    if (requestOK(resAction, addQuestionnaire)) {
      const result = unwrapResult(resAction);
      if (result?.booking) {
        const type = result?.slotTime;
        dispatch(setUserQuestionnaireId({id: result?.medical?.id}));
        history.push(`/appointment?type=${type}&methodKey=${METHOD_KEYS.BIRTH_CONTROL_REQUEST}&token=${savedToken}`);
      }
    }
  };

  const onChangeCarousel = (pos: number) => {
    setInputs({productId: packSizeList[pos]?.productId});
  };

  const renderDeliveryInfo = () => {
    const deliverInfoKey = packSizeList[selectedItemIndex]?.deliveryInfoKey;
    const deliveryDesc = packSizeList[selectedItemIndex]?.deliveryDescription;
    if (deliverInfoKey === FREE_DELIVERY_KEY) {
      return (
        <>
          <Col style={{backgroundColor: '#E7CBB1', width: '70%', borderRadius: 50}} py={2}>
            <UiText variant="small" fontWeight="700">
              {questionnaire?.deliveryInfo?.find(info => info.key === FREE_DELIVERY_KEY)?.value}
            </UiText>
          </Col>
          {!!deliveryDesc && (
            <UiText w={{base: '100%', md: '65%'}} mt={4} variant="small">
              {deliveryDesc}
            </UiText>
          )}
        </>
      );
    }
    if (deliverInfoKey === STANDARD_DELIVERY_KEY) {
      return (
        <Col>
          <UiText textAlign="center" variant="small">
            {questionnaire?.deliveryInfo?.find(info => info.key === STANDARD_DELIVERY_KEY)?.value}
          </UiText>
          {!!deliveryDesc && (
            <UiText w={{base: '100%', md: '65%'}} mt={4} variant="small">
              {deliveryDesc}
            </UiText>
          )}
        </Col>
      );
    }
    return null;
  };

  const handleGoBack = () => {
    const questionnaireAnswer = {
      key: BIRTH_CONTROL_REQUEST_KEYS.CHOOSE_PRODUCT_PACKAGE,
      index: questionnaire?.index || '',
      data: {},
    };
    dispatch(setQuestionnaireAnswer(questionnaireAnswer));
  };

  const renderCarouselItem = (item: PackItem[]) =>
    item?.map((i, index) => (
      <UiCard
        variant="rounded"
        py={inputs.productId === i?.productId ? spacing.small : spacing.tiny}
        px={spacing.mini}
        key={i?.value}
        minWidth="40%"
        my={inputs.productId === i?.productId ? spacing.none : spacing.tiny}
        boxShadow="lg"
        borderRadius="2xl"
        transition="300ms"
        _hover={{cursor: 'pointer'}}
        opacity={selectedItemIndex === index ? 1 : 0.3}
        style={inputs.productId !== i?.productId ? {scale: '0.9'} : {scale: '1'}}
      >
        <UiText fontSize="xl" fontWeight="600">
          {i?.title}
        </UiText>
        <Row alignItems="center" mt={spacing.tiny}>
          <UiText fontSize="xl" fontWeight="600">
            ${i?.pricePerPack}*
          </UiText>
          <UiText fontWeight="600">&nbsp; / pack</UiText>
        </Row>
        <UiImage
          type="icon"
          name={i?.icon}
          boxSize={isBirthControlPatchSelected ? spacing.medium : spacing.large}
          objectFit="contain"
        />
        <UiText>total price:</UiText>
        <UiText fontWeight="700">${i?.totalPrice}*</UiText>
        <UiText textAlign="center" mt={spacing.tiny}>
          {i?.description}
        </UiText>
        {i?.description ? (
          <UiImage type="icon" name="atome" maxWidth={spacing.standard} boxSize={spacing.small} mt={spacing.tiny} />
        ) : (
          <UiBox height={spacing.mLarge} />
        )}
      </UiCard>
    ));

  return (
    <UiQuestionnaireLayout
      actions={questionnaire?.actions}
      disableNext={!inputs.productId}
      handleGoBack={handleGoBack}
      handleOnNext={onClickSubmit}
      loading={loading}
      checkoutStep={questionnaire?.checkoutStep}
    >
      <Col alignItems="center" textAlign="center">
        <UiImage
          type="icon"
          name={isBirthControlPatchSelected ? PATCH_TOP_ICON : questionnaire?.icon}
          boxSize={spacing.mLarge}
          mt={spacing.small}
        />
        <UiText fontSize="xl" fontWeight="bold" mt={spacing.medium}>
          {questionnaire?.questions?.default}
        </UiText>
        <UiText fontSize="xl" fontWeight="600" my={spacing.small} color="fadedJade">
          {medicationAnswer?.name}
        </UiText>
        <UiBox w="100%" className="pack-size">
          <AppCarousel
            selectedItem={selectedItemIndex >= 0 ? selectedItemIndex : 0}
            onChange={onChangeCarousel}
            onClickItem={index => onChangeCarousel(index)}
            showIndicators={false}
            showArrows={false}
            showStatus={false}
            showThumbs={false}
            centerMode
            centerSlidePercentage={isDesktop ? 45 : 57}
            emulateTouch
            preventMovementUntilSwipeScrollTolerance={true}
            swipeScrollTolerance={50}
          >
            {renderCarouselItem(packSizeList)}
          </AppCarousel>
        </UiBox>
        {renderDeliveryInfo()}
        <UiText fontSize="xs" color="fadedJade" mt={spacing.xmedium}>
          {questionnaire?.descriptions?.sub}
        </UiText>
      </Col>
    </UiQuestionnaireLayout>
  );
};

export default Questionnaire13Screen;
